import {
  Box,
  Button,
  Card,
  CardBody,
  ChakraProvider,
  Container,
  Flex,
  Grid,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { Check, Download, Eye, Menu } from "react-feather";
import { useReactToPrint } from "react-to-print";
import { useCurrentProfile } from "../../ContextWrapper";

import { endOfMonth, format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  getGrowthPlanData,
  getPlValues,
  getResourcePerformance,
} from "../../api/growthPlan";
import { fallbackDate } from "../../config/dashboardDefaults";

import theme from "../../theme";
import {
  colorExpenseFormat,
  colorFormat,
  customCurrencyFormat,
} from "../../utils";
import { getCustomerDashboardDate } from "../../utils/getCustomerDashboardDate";
import YtdToggle from "../dashboardComponents/ytdToggle";
import BankLinkNotificationContainer from "../generic/linkBankNotification";
import NotificationContainer from "../generic/updateNotification";
import { PdfContent } from "../reports/performanceReviewReport";
import UncategorizedNotificationBanner from "../uncategorized/uncategorizedNotificationBanner";
import DashboardDropdown from "./dashboardDateComponent";
import ClientDashboardLayout from "./dashboardLayout";
import ExpenseCard from "./expenseCard";
import GrowthPlanCard from "./growthPlanCard";
import ProfitCard from "./profitCard";
import axios from "axios";
import TargetTypeToggle from "../dashboardComponents/targetToggle";

const ResourceCard = ({ title, description, tag, listing, column }) => {
  const navigate = useNavigate();
  return (
    <Stack
      fontFamily={"lato"}
      width="100%"
      align="center"
      gridColumn={column}
      p="15px 10px"
      position={"relative"}
    >
      <Card
        width="100%"
        height="111px"
        borderRadius="12px"
        position="relative"
        role="group"
        cursor={"pointer"}
        _hover={{ boxShadow: "lg" }}
        onClick={() => {
          const tagId = Number(tag);
          navigate(`/vault?tag=${isNaN(tagId) ? "" : tagId}`);
        }}
      >
        {/* <Image
          src="/resource-arrow.png"
          alt="Client Navigation Open"
          boxSize="35px"
          objectFit="contain"
          position="absolute"
          top="10px"
          right="15px"
          _groupHover={{
            filter: "brightness(1.2)",
          }}
        /> */}
        <CardBody textAlign="center" pb={0}>
          <Box p="10px 0 0 0">
            <Text fontSize="20px" lineHeight={"21px"} fontWeight={600} pb={1}>
              {title}
            </Text>
            <Text fontSize={"14px"}>{description}.</Text>
          </Box>
        </CardBody>
      </Card>

      <Card
        position="absolute"
        top="-15px"
        left="50%"
        transform="translateX(-50%)"
        width="50px"
        height="50px"
        borderRadius="100%"
        bg="#536DEC"
      >
        <CardBody p={"10px 0 0 0"}>
          <Text textAlign="center" fontSize={"20px"} color="white">
            {listing}
          </Text>
        </CardBody>
      </Card>
    </Stack>
  );
};

const ResourceOverview = ({ resourceOverviewData }) => {
  const [isMobile] = useMediaQuery("(max-width: 1280px)");

  const recommendedResources =
    resourceOverviewData?.recommended_resources?.filter(
      (resource) => resource !== undefined && resource !== null,
    ) || [];

  const calculateColumns = () => {
    if (recommendedResources.length === 1) {
      return { base: "1fr", md: "1fr", lg: "1fr" };
    } else if (recommendedResources.length === 2) {
      return { base: "1fr", md: "1fr", lg: "1fr 1fr" };
    } else if (recommendedResources.length === 3) {
      return { base: "1fr", md: "1fr", lg: "1fr 1fr 1fr" };
    } else {
      return { base: "1fr" };
    }
  };

  const renderResourceCards = ({ resources }) => {
    return resources.map((resource, index) => (
      <ResourceCard
        key={index}
        title={resource.name}
        description={resource.description}
        tag={resource.tag}
        listing={index + 1}
      />
    ));
  };

  return (
    <Stack p={"10px 0 0 0"}>
      {recommendedResources.length > 0 && (
        <SimpleGrid
          templateColumns={calculateColumns()}
          spacing={2}
          p="5px"
          alignItems="center"
          justifyItems="center"
        >
          {renderResourceCards({
            resources: recommendedResources,
          })}
        </SimpleGrid>
      )}

      {resourceOverviewData?.performance_overview && (
        <Stack
          width="100%"
          align="center"
          p={!isMobile ? "0px 15px 10px 15px" : "0px 10px 10px"}
          fontFamily={"lato"}
        >
          <Card
            width="100%"
            minHeight="171px"
            maxH={"100%"}
            borderRadius="12px"
          >
            <CardBody textAlign={"center"}>
              <Box>
                <Text fontSize={"24px"} lineHeight={"10px"}>
                  Performance Overview
                </Text>
                <Stack
                  as="span"
                  display="inline-block"
                  borderTop="2px solid #536DEC"
                  width="150px"
                  textAlign="center"
                />
              </Box>
              <Text fontSize={"16px"}>
                {resourceOverviewData.performance_overview}
              </Text>
            </CardBody>
          </Card>
        </Stack>
      )}
    </Stack>
  );
};

const GrowthPlanContent = ({
  customer_id,
  date,
  customer_type = null,
  customer,
  adjusted,
}) => {
  const { profile } = useCurrentProfile();
  const formattedDate = format(date, "yyyy-MM-dd");
  const [growthPlanData, setGrowthPlanData] = useState({
    income: "-",
    income_forecast: "-",
    income_forecast_percentage: "-",
    cash_on_hand: "-",
    last_cash_on_hand: "-",
    last_cash_on_hand_percentage: "-",
    expense: "-",
    expense_forecast: "-",
    expense_forecast_percentage: "-",
    equity: "-",
    last_equity: "-",
    last_equity_percentage: "-",
    profit: "-",
    profit_percentage: "-",
    team_percentage: "-",
    marketing_percentage: "-",
    overhead_percentage: "-",
    cogs_percentage: "-",
    team: "-",
    marketing: "-",
    overhead: "-",
    cogs: "-",
  });

  const [resourceOverview, setResourceOverview] = useState({
    first_recommended_resource: null,
    second_recommended_resource: null,
    third_recommended_resource: null,
    performance_overview: null,
  });

  const [pLData, setPLData] = useState({
    overhead_pl: "-",
    team_pl: "-",
    profit_pl: "-",
    marketing_pl: "-",
    cogs_pl: "-",
  });
  const [isMobile] = useMediaQuery("(max-width: 900px)", { ssr: false });
  const [mobileHeader] = useMediaQuery("(max-width: 1280px)", { ssr: false });

  const [ytd, setYtd] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ytd") || "false");
    } catch {
      return false;
    }
  });

  const [customTarget, setCustomTarget] = useState(
    JSON.parse(localStorage.getItem("isCustomTarget") || "false"),
  );

  useEffect(() => {
    const handleTargetTypeChange = (event) => {
      setCustomTarget(event.detail);
    };

    window.addEventListener("targetTypeChanged", handleTargetTypeChange);

    return () => {
      window.removeEventListener("targetTypeChanged", handleTargetTypeChange);
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      const ytd = JSON.parse(sessionStorage.getItem("ytd") || "false");
      setYtd(ytd);
    };

    window.addEventListener("ytd-changed", handler);
    return () => window.removeEventListener("ytd-changed", handler);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshGrowthPlanData = async (id) => {
      if (!id) return;
      try {
        const res = await getGrowthPlanData(
          id,
          formattedDate,
          controller.signal,
          { adjusted, ytd, customTarget },
        );
        if (isActive) setGrowthPlanData(res.data);
      } catch (err) {
        if (err?.response) console.log(err);
      }
    };

    refreshGrowthPlanData(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, adjusted, ytd, customTarget]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshResourceOverview = async (id) => {
      if (!id) return;
      try {
        const res = await getResourcePerformance(
          id,
          formattedDate,
          controller.signal,
          { adjusted, ytd, customTarget },
        );
        if (isActive) setResourceOverview(res.data);
      } catch (err) {
        if (err?.response) console.log(err);
      }
    };

    refreshResourceOverview(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, adjusted, ytd, customTarget]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshPLData = async (id) => {
      if (!id) return;
      try {
        const res = await getPlValues(id, formattedDate, controller.signal, {
          adjusted,
          ytd,
          customTarget,
        });
        if (isActive) setPLData(res.data);
      } catch (err) {
        if (err?.response) console.log(err);
      }
    };

    refreshPLData(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, adjusted, ytd, customTarget]);

  return (
    <Stack
      mt={mobileHeader ? "10px" : "72px"}
      p={isMobile ? "10px 10px" : "0px"}
      fontFamily={"lato"}
    >
      {profile?.type === "client" && (
        <Box>
          <BankLinkNotificationContainer customer={customer} />
        </Box>
      )}
      <NotificationContainer rounded={true} p={"20px 15px 2px"} />

      {profile?.type === "client" && (
        <UncategorizedNotificationBanner customerId={customer_id} />
      )}
      <Stack p="19px 15px 0px 15px">
        <YtdToggle></YtdToggle>
      </Stack>
      {!isMobile && (
        <Grid
          templateColumns="2fr 248px 2fr"
          rowGap="0"
          columnGap="10px"
          p="10px 5px 0px 5px"
          alignItems="center"
          justifyItems="center"
        >
          <Stack
            width="100%"
            align="center"
            gridColumn={1}
            gridRow={1}
            p="10px"
          >
            <GrowthPlanCard
              leftTitle="Total Income"
              rightTitle="Target"
              leftAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.income : "-"}`,
              )}
              rightAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.income_forecast : "-"}`,
              )}
              rightPercentage={customCurrencyFormat(
                `${
                  growthPlanData
                    ? growthPlanData.income_forecast_percentage
                    : "-"
                }`,
                true,
              )}
              cardHeight="108px"
              cardWidth="100%"
              color={colorFormat(
                growthPlanData
                  ? growthPlanData.income_forecast_percentage
                  : "0.00",
              )}
              image={"/money-up-arrow.png"}
              type="income"
            />
          </Stack>

          <Stack
            width="100%"
            align="center"
            gridColumn={1}
            gridRow={2}
            p="10px" // Reduce padding here
          >
            <GrowthPlanCard
              leftTitle="Cash on Hand"
              rightTitle="Last Month"
              leftAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.cash_on_hand : "-"}`,
              )}
              rightAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.last_cash_on_hand : "-"}`,
              )}
              rightPercentage={customCurrencyFormat(
                `${
                  growthPlanData
                    ? growthPlanData.last_cash_on_hand_percentage
                    : "-"
                }`,
                true,
              )}
              cardHeight="108px"
              cardWidth="100%"
              color={colorFormat(
                growthPlanData
                  ? growthPlanData.last_cash_on_hand_percentage
                  : "0.00",
              )}
              image={"/money-bag.png"}
              isLocked={customer_type === "trial" ? true : false}
            />
          </Stack>

          <Stack align="center" gridColumn={2} gridRow="span 2" width="320px">
            <ProfitCard
              profit={customCurrencyFormat(
                growthPlanData ? growthPlanData.profit : "-",
              )}
              profitPercentage={customCurrencyFormat(
                growthPlanData ? growthPlanData.profit_percentage : "-",
                true,
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.profit_pl : "-",
                true,
              )}
              color={colorExpenseFormat(
                pLData ? pLData.profit_pl : "-",
                growthPlanData ? growthPlanData.profit_percentage : "0.00",
                true,
                true,
              )}
              status={true}
              data={growthPlanData ? growthPlanData : {}}
            />
          </Stack>

          <Stack
            width="100%"
            align="center"
            gridColumn={3}
            gridRow={1}
            p="10px" // Reduce padding here
          >
            <GrowthPlanCard
              leftTitle="Total Expenses"
              rightTitle="Target"
              leftAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.expense : "-"}`,
              )}
              rightAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.expense_forecast : "-"}`,
              )}
              rightPercentage={customCurrencyFormat(
                `${
                  growthPlanData
                    ? growthPlanData.expense_forecast_percentage
                    : "-"
                }`,
                true,
              )}
              cardHeight="108px"
              cardWidth="100%"
              color={colorFormat(
                growthPlanData
                  ? growthPlanData.expense_forecast_percentage
                  : "0.00",
                true,
              )}
              image={"/money-down-arrow.png"}
              type="expense"
            />
          </Stack>

          <Stack
            width="100%"
            align="center"
            gridColumn={3}
            gridRow={2}
            p="10px" // Reduce padding here
          >
            <GrowthPlanCard
              leftTitle="Equity"
              rightTitle="Last Month"
              leftAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.equity : "-"}`,
              )}
              rightAmount={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.last_equity : "-"}`,
              )}
              rightPercentage={customCurrencyFormat(
                `${
                  growthPlanData ? growthPlanData.last_equity_percentage : "-"
                }`,
                true,
              )}
              cardHeight="108px"
              cardWidth="100%"
              color={colorFormat(
                growthPlanData ? growthPlanData.last_equity_percentage : 0.0,
              )}
              image={"/bar-chart-with-arrow.png"}
              isLocked={customer_type === "trial"}
            />
          </Stack>
        </Grid>
      )}
      {isMobile && (
        <VStack align="center" justify="center" w="100%" h="100%" pt="20px">
          <Stack width="100%" align="center" mb={"15px"}>
            <ProfitCard
              profit={customCurrencyFormat(
                growthPlanData ? growthPlanData.profit : "-",
              )}
              profitPercentage={customCurrencyFormat(
                growthPlanData ? growthPlanData.profit_percentage : "-",
                true,
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.profit_pl : "-",
                true,
              )}
              color={colorExpenseFormat(
                pLData ? pLData.profit_pl : "0.00",
                growthPlanData ? growthPlanData.profit_percentage : "0.00",
                true,
                true,
              )}
              status={true}
              data={growthPlanData ? growthPlanData : {}}
            />
          </Stack>
          <Grid
            w="100%"
            h="100%"
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
            }}
          >
            <Stack width="100%" align="center" p="10px 10px">
              <GrowthPlanCard
                leftTitle="Total Income"
                rightTitle="Target"
                leftAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.income : "-"}`,
                )}
                rightAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.income_forecast : "-"}`,
                )}
                rightPercentage={customCurrencyFormat(
                  `${
                    growthPlanData
                      ? growthPlanData.income_forecast_percentage
                      : "-"
                  }`,
                  true,
                )}
                cardHeight="120px"
                cardWidth="100%"
                color={colorFormat(
                  growthPlanData
                    ? growthPlanData.income_forecast_percentage
                    : "0.00",
                )}
                image={"/money-up-arrow.png"}
                type="income"
              />
            </Stack>
            <Stack width="100%" align="center" p="10px 10px">
              <GrowthPlanCard
                leftTitle="Total Expenses"
                rightTitle="Target"
                leftAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.expense : "-"}`,
                )}
                rightAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.expense_forecast : "-"}`,
                )}
                rightPercentage={customCurrencyFormat(
                  `${
                    growthPlanData
                      ? growthPlanData.expense_forecast_percentage
                      : "-"
                  }`,
                  true,
                )}
                cardHeight="120px"
                cardWidth="100%"
                color={colorFormat(
                  growthPlanData
                    ? growthPlanData.expense_forecast_percentage
                    : "0.00",
                  true,
                )}
                image={"/money-down-arrow.png"}
                type="expense"
              />
            </Stack>
            <Stack width="100%" align="center" p="10px 10px">
              <GrowthPlanCard
                leftTitle="Cash on Hand"
                rightTitle="Last Month"
                leftAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.cash_on_hand : "-"}`,
                )}
                rightAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.last_cash_on_hand : "-"}`,
                )}
                rightPercentage={customCurrencyFormat(
                  `${
                    growthPlanData
                      ? growthPlanData.last_cash_on_hand_percentage
                      : "-"
                  }`,
                  true,
                )}
                cardHeight="120px"
                cardWidth="100%"
                color={colorFormat(
                  growthPlanData
                    ? growthPlanData.last_cash_on_hand_percentage
                    : "0.00",
                )}
                image={"/money-bag.png"}
                isLocked={customer_type === "trial"}
              />
            </Stack>
            <Stack width="100%" align="center" p="10px 10px">
              <GrowthPlanCard
                leftTitle="Equity"
                rightTitle="Last Month"
                leftAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.equity : "-"}`,
                )}
                rightAmount={customCurrencyFormat(
                  `${growthPlanData ? growthPlanData.last_equity : "-"}`,
                )}
                rightPercentage={customCurrencyFormat(
                  `${
                    growthPlanData ? growthPlanData.last_equity_percentage : "-"
                  }`,
                  true,
                )}
                cardHeight="120px"
                cardWidth="100%"
                color={colorFormat(
                  growthPlanData
                    ? growthPlanData.last_equity_percentage
                    : "0.00",
                )}
                image={"/bar-chart-with-arrow.png"}
                isLocked={customer_type === "trial"}
              />
            </Stack>
          </Grid>
        </VStack>
      )}
      {!isMobile ? (
        <SimpleGrid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "1fr 1fr 1fr 1fr",
          }}
          spacing={2}
          p="5px 5px 15px 5px"
          alignItems="center"
          justifyItems="center"
        >
          <ExpenseCard
            title="Team"
            percentage={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.team_percentage : "-"}`,
              true,
              false,
              true,
              true,
            )}
            total={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.team : "-"}`,
            )}
            perfectPL={customCurrencyFormat(
              pLData ? pLData.team_pl : "-",
              true,
            )}
            color={colorExpenseFormat(
              pLData ? pLData.team_pl : "0.00",
              growthPlanData ? growthPlanData.team_percentage : "0.00",
            )}
            type="team"
          />
          <ExpenseCard
            title="Marketing"
            percentage={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.marketing_percentage : "-"}`,
              true,
              false,
              true,
              true,
            )}
            total={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.marketing : "-"}`,
            )}
            perfectPL={customCurrencyFormat(
              pLData ? pLData.marketing_pl : "-",
              true,
            )}
            color={colorExpenseFormat(
              pLData ? pLData.marketing_pl : "0.00",
              growthPlanData ? growthPlanData.marketing_percentage : "0.00",
            )}
            type="marketing"
          />
          <ExpenseCard
            title="Operations"
            percentage={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.overhead_percentage : "-"}`,
              true,
              false,
              true,
              true,
            )}
            total={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.overhead : "-"}`,
            )}
            perfectPL={customCurrencyFormat(
              pLData ? pLData.overhead_pl : "-",
              true,
            )}
            color={colorExpenseFormat(
              pLData ? pLData.overhead_pl : "0.00",
              growthPlanData ? growthPlanData.overhead_percentage : "0.00",
            )}
            type="overhead"
          />
          <ExpenseCard
            title="Products"
            percentage={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.cogs_percentage : "-"}`,
              true,
              false,
              true,
              true,
            )}
            total={customCurrencyFormat(
              `${growthPlanData ? growthPlanData.cogs : "-"}`,
            )}
            perfectPL={customCurrencyFormat(
              pLData ? pLData.cogs_pl : "-",
              true,
            )}
            color={colorExpenseFormat(
              pLData ? pLData.cogs_pl : "0.00",
              growthPlanData ? growthPlanData.cogs_percentage : "0.00",
            )}
            type="cogs"
          />
        </SimpleGrid>
      ) : (
        <VStack margin={"15px 0px"}>
          <Grid
            w="100%"
            h="100%"
            paddingTop={"5px"}
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
            }}
          >
            <ExpenseCard
              title="Team"
              percentage={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.team_percentage : "-"}`,
                true,
                false,
                true,
                true,
              )}
              total={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.team : "-"}`,
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.team_pl : "-",
                true,
              )}
              color={colorExpenseFormat(
                pLData ? pLData.team_pl : "0.00",
                growthPlanData ? growthPlanData.team_percentage : "0.00",
              )}
              type="team"
            />
            <ExpenseCard
              title="Marketing"
              percentage={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.marketing_percentage : "-"}`,
                true,
                false,
                true,
                true,
              )}
              total={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.marketing : "-"}`,
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.marketing_pl : "-",
                true,
              )}
              color={colorExpenseFormat(
                pLData ? pLData.marketing_pl : "0.00",
                growthPlanData ? growthPlanData.marketing_percentage : "0.00",
              )}
              type="marketing"
            />
            <ExpenseCard
              title="Operations"
              percentage={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.overhead_percentage : "-"}`,
                true,
                false,
                true,
                true,
              )}
              total={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.overhead : "-"}`,
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.overhead_pl : "-",
                true,
              )}
              color={colorExpenseFormat(
                pLData ? pLData.overhead_pl : "0.00",
                growthPlanData ? growthPlanData.overhead_percentage : "0.00",
              )}
              type="overhead"
            />
            <ExpenseCard
              title="Products"
              percentage={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.cogs_percentage : "-"}`,
                true,
                false,
                true,
                true,
              )}
              total={customCurrencyFormat(
                `${growthPlanData ? growthPlanData.cogs : "-"}`,
              )}
              perfectPL={customCurrencyFormat(
                pLData ? pLData.cogs_pl : "-",
                true,
              )}
              color={colorExpenseFormat(
                pLData ? pLData.cogs_pl : "0.00",
                growthPlanData ? growthPlanData.cogs_percentage : "0.00",
              )}
              type="cogs"
            />
          </Grid>
        </VStack>
      )}
      <ResourceOverview resourceOverviewData={resourceOverview} />
    </Stack>
  );
};

export const GrowthPlan = () => {
  const { profile } = useCurrentProfile();

  const [activeCustomer, setActiveCustomer] = useState(null);

  useEffect(() => {
    if (!profile) return;

    const storedId = sessionStorage.getItem("selectedCustomerId");
    if (!storedId) return;

    if (profile.type !== "admin") {
      const customer =
        profile.customers.find((c) => c.id.toString() === storedId) ||
        profile.customers[0] ||
        null;
      setActiveCustomer(customer);
      return;
    }

    const fetchCustomer = async () => {
      try {
        const res = await axios.get(
          `/api/v2/customers/${storedId}/`,
          { headers: { "Content-Type": "application/json" } },
          { withCredentials: true },
        );
        setActiveCustomer(res.data);
      } catch (err) {
        console.error("Error fetching admin customer:", err);
      }
    };

    fetchCustomer();
  }, [profile]);

  useEffect(() => {
    const handleSelectedChange = async () => {
      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      if (profile.type !== "admin") {
        const found = profile.customers.find(
          (c) => c.id.toString() === storedId,
        );
        if (found) setActiveCustomer(found);
      } else {
        try {
          const res = await axios.get(`/api/v2/customers/${storedId}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          });
          setActiveCustomer(res.data);
        } catch (err) {
          console.error("Error fetching admin customer:", err);
        }
      }
    };

    window.addEventListener("selectedCustomerId-changed", handleSelectedChange);
    return () => {
      window.removeEventListener(
        "selectedCustomerId-changed",
        handleSelectedChange,
      );
    };
  }, [profile]);

  const customer_type = activeCustomer?.customer_type;

  const customer = activeCustomer;
  const customer_id = activeCustomer?.id;

  const [initialDashDate, setInitialDashDate] = useState(() => {
    return getCustomerDashboardDate(customer);
  });

  const [dashboardDate, setDashboardDate] = useState(() => {
    const stored = localStorage.getItem("dashboardDate");

    if (stored) {
      const [y, m, d] = stored.split("-").map(Number);
      return new Date(y, m - 1, d);
    }

    return getCustomerDashboardDate(customer);
  });

  const [dateCategory, setDateCategory] = useState(() => {
    const stored = localStorage.getItem("dashboardDate");

    if (stored) {
      const d = new Date(stored);
      return format(d, "yyyy-MM");
    }

    const date = getCustomerDashboardDate(customer);
    return format(date, "yyyy-MM");
  });

  useEffect(() => {
    if (!activeCustomer) return;

    const stored = localStorage.getItem("dashboardDate");
    const newDate = stored
      ? new Date(stored.split("-").map(Number))
      : getCustomerDashboardDate(activeCustomer);

    setDashDate(newDate);
    setDashboardDate(newDate);
    setDateCategory(format(newDate, "yyyy-MM"));
  }, [activeCustomer]);

  useEffect(() => {
    const handleDashboardDateChange = () => {
      const stored = localStorage.getItem("dashboardDate");

      let dateObj;
      if (stored) {
        const [y, m, d] = stored.split("-").map(Number);
        dateObj = new Date(y, m - 1, d);
      } else {
        dateObj = getCustomerDashboardDate(activeCustomer);
      }

      setDashboardDate(dateObj);
      setInitialDashDate(dateObj);
      setDateCategory(format(dateObj, "yyyy-MM"));
    };

    window.addEventListener("dashboardDate-changed", handleDashboardDateChange);

    return () => {
      window.removeEventListener(
        "dashboardDate-changed",
        handleDashboardDateChange,
      );
    };
  }, [activeCustomer]);

  const defaultDate = customer?.dashboard_date
    ? new Date(endOfMonth(parseISO(customer.dashboard_date)).toDateString())
    : fallbackDate;
  const [dashDate, setDashDate] = useState(initialDashDate);

  //---------------------------------------------------------

  const [isMobile] = useMediaQuery("(max-width: 1280px)", { ssr: false });

  useEffect(() => {
    const finalDate = dashboardDate
      ? new Date(endOfMonth(new Date(dashboardDate)).toDateString())
      : fallbackDate;

    setDashDate(finalDate);
  }, [dashboardDate]);

  const updateDate = (newDate) => {
    setDashDate(new Date(newDate));
    if (setDashboardDate) {
      setDashboardDate(newDate);
    }
  };

  const reportShortDate = dashDate
    .toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    })
    .toUpperCase();

  const [showPdfContent, setShowPdfContent] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [loading, setLoading] = useState(false);

  const printRef = useRef(null);

  const handleBeforePrint = () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 500);
    });
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
    @page {
      size: A4 portrait;
      margin: 0;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
      }
      canvas {
        image-rendering: crisp-edges;
        width: 6in;
        height: 4in;
        display: block;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
    documentTitle: `Performance Review ${reportShortDate}`,
    onBeforeGetContent: handleBeforePrint,
    onAfterPrint: () => {
      setShowPdfContent(false);
      setPdfReady(false);
      setLoading(false);
    },
  });

  useEffect(() => {
    if (showPdfContent && pdfReady) {
      handlePrint();
    }
  }, [showPdfContent, pdfReady, handlePrint]);

  const onDownloadClick = () => {
    setLoading(true);
    setShowPdfContent(true);
  };

  useEffect(() => {
    if (!showPdfContent || !printRef.current) return;

    setPdfReady(false);
    setLoading(true);

    let timeoutId = null;

    const done = () => {
      setPdfReady(true);
      setLoading(false);
    };

    const observerMutation = new MutationObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(done, 6000);
    });

    const observerResize = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(done, 6000);
    });

    observerMutation.observe(printRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    observerResize.observe(printRef.current);

    const forceRedraw = () => {
      if (printRef.current) {
        const event = new Event("resize");
        window.dispatchEvent(event);
      }
    };

    const redrawTimeout = setTimeout(forceRedraw, 300);

    return () => {
      observerMutation.disconnect();
      observerResize.disconnect();
      clearTimeout(timeoutId);
      clearTimeout(redrawTimeout);
    };
  }, [showPdfContent, customer, dashDate]);

  useEffect(() => {
    if (showPdfContent && pdfReady) {
      const delay = setTimeout(() => {
        handlePrint();
      }, 6000);

      return () => clearTimeout(delay);
    }
  }, [showPdfContent, pdfReady, handlePrint]);

  const [adjustedView, setAdjustedView] = useState(() => {
    const stored = sessionStorage.getItem("adjustedView");
    return stored ? JSON.parse(stored) : false;
  });

  const handleToggleAdjusted = () => {
    setAdjustedView((prev) => {
      const next = !prev;
      sessionStorage.setItem("adjustedView", JSON.stringify(next));

      window.dispatchEvent(
        new CustomEvent("adjustedView-changed", { detail: next }),
      );

      return next;
    });
  };

  useEffect(() => {
    const handler = (e) => {
      setAdjustedView(e.detail);
      console.log(e);
    };

    window.addEventListener("adjustedView-changed", handler);

    return () => window.removeEventListener("adjustedView-changed", handler);
  }, []);

  return (
    <ClientDashboardLayout>
      <ChakraProvider theme={theme}>
        {showPdfContent && (
          <Box
            position="fixed"
            top="-10000px"
            left="-10000px"
            width="210mm"
            height="297mm"
            overflow="hidden"
          >
            <PdfContent
              customer={customer}
              externalEndDate={dashDate}
              hideButtons={true}
              externalPrintRef={printRef}
              trial={customer_type === "trial" ? true : false}
            />
          </Box>
        )}
        <Container
          maxW="100%"
          bg="#F4F4F2"
          minH="100vh"
          p="0px"
          overflowX={"hidden"}
          position={"relative"}
        >
          {isMobile && (
            <Card
              position="fixed"
              zIndex={999}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h={"100%"}
                w="100%"
                px={4}
              >
                <Button
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  w="50px"
                  h="50px"
                  p={2}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <Menu
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>

                <HStack>
                  <DashboardDropdown
                    key={dashDate?.toISOString()}
                    height="36px"
                    setEnd={updateDate}
                    dateFromNav={defaultDate}
                    endDate={dashDate}
                    category={dateCategory}
                    setCategory={setDateCategory}
                    monthAmount={customer_type === "trial" ? 3 : 12}
                    includeAllFromMin={customer_type === "trial" ? false : true}
                  />
                  {activeCustomer?.is_demo ===
                    false &&
                    (
                      <Box ml={"10px"}>
                        <TargetTypeToggle></TargetTypeToggle>
                      </Box>
                    )}

                  <Button
                    ml="10px"
                    p="0"
                    minW="36px"
                    height="36px"
                    onClick={onDownloadClick}
                    isDisabled={loading}
                  >
                    {!loading ? (
                      <Download size="20px" />
                    ) : (
                      <Spinner
                        thickness="2px"
                        speed="0.5s"
                        emptyColor="gray.200"
                        color="#536DEC"
                        size="sm"
                      />
                    )}
                  </Button>

                  {(profile?.type === "admin" || customer_type === "trial") && (
                    <IconButton
                      ml="10px"
                      aria-label="Toggle Adjusted View"
                      icon={
                        adjustedView ? <Check size={18} /> : <Eye size={18} />
                      }
                      onClick={handleToggleAdjusted}
                      variant={adjustedView ? "solid" : "outline"}
                      size="sm"
                      height="36px"
                      width={"36px"}
                    />
                  )}
                </HStack>
              </HStack>
            </Card>
          )}

          <Card
            position={"fixed"}
            zIndex={301}
            borderRadius={0}
            mt={isMobile ? "72px" : "0px"}
            h={isMobile ? "0px" : "72px"}
            borderBottom="1px solid #e0e0e0"
            width={isMobile ? "100%" : "auto"}
            marginBottom={100}
          >
            {!isMobile && (
              <Grid
                templateColumns="1fr 2fr 1fr 275fr"
                width="100%"
                alignItems="center"
                p="0px 20px"
                h={isMobile ? "50px" : "72px"}
              >
                <Text
                  whiteSpace={"nowrap"}
                  fontSize={"24px"}
                  fontWeight={600}
                  color={"#30384A"}
                >
                  Growth Plan
                </Text>

                <Flex
                  gridColumn={9}
                  textAlign="right"
                  alignItems="center"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <DashboardDropdown
                    key={dashDate?.toISOString()}
                    height="36px"
                    dateFromNav={defaultDate}
                    setEnd={updateDate}
                    endDate={dashDate}
                    category={dateCategory}
                    setCategory={setDateCategory}
                    monthAmount={customer_type === "trial" ? 3 : 12}
                    includeAllFromMin={customer_type === "trial" ? false : true}
                  />


                  {activeCustomer?.is_demo ===
                    false &&
                    (
                  <Box ml={"10px"}>
                    <TargetTypeToggle></TargetTypeToggle>
                  </Box>)}

                  <Button
                    ml="10px"
                    p="0"
                    minW="36px"
                    height="36px"
                    onClick={onDownloadClick}
                    isDisabled={loading}
                  >
                    {!loading ? (
                      <Download size="20px" />
                    ) : (
                      <Spinner
                        thickness="2px"
                        speed="0.5s"
                        emptyColor="gray.200"
                        color="#536DEC"
                        size="sm"
                      />
                    )}
                  </Button>
                  {(profile?.type === "admin" || customer_type === "trial") && (
                    <IconButton
                      ml="10px"
                      aria-label="Toggle Adjusted View"
                      icon={
                        adjustedView ? <Check size={18} /> : <Eye size={18} />
                      }
                      onClick={handleToggleAdjusted}
                      variant={adjustedView ? "solid" : "outline"}
                      size="sm"
                      height="36px"
                      width={"36px"}
                    />
                  )}
                </Flex>
              </Grid>
            )}
          </Card>

          <Stack mt={isMobile ? "72px" : "0px"}>
            <GrowthPlanContent
              customer_id={customer_id}
              date={dashDate}
              customer_type={customer_type}
              customer={customer}
              adjusted={adjustedView}
            />
          </Stack>
        </Container>
      </ChakraProvider>
    </ClientDashboardLayout>
  );
};
